import React, { useEffect, useRef, useState } from "react";
import type { PreviewItem } from "../../hooks/useMediaLightbox";

type Props = {
  imagePreview: PreviewItem | null;
  imagePreviewList: PreviewItem[];
  imagePreviewIndex: number;
  imagePreviewVisible: boolean;
  onClose: () => void;
  onSelectIndex: (idx: number) => void;
  onSelectPreview: (preview: PreviewItem | null) => void;
};

const ANIM_MS = 260;
const SWIPE_X_THRESHOLD = 50;   // px to commit a horizontal swipe
const SWIPE_Y_THRESHOLD = 80;   // px to commit a vertical swipe-to-close

function filenameFromPreview(name: string, url: string) {
  const fallback = "image";
  const rawName = String(name || "").trim();
  const urlName = (() => {
    try {
      const path = new URL(url, window.location.href).pathname;
      return decodeURIComponent(path.split("/").filter(Boolean).pop() || "");
    } catch { return ""; }
  })();
  const base = rawName && rawName !== fallback ? rawName : urlName || fallback;
  return base.replace(/[\\/:*?"<>|]+/g, "_").slice(0, 180) || fallback;
}

export default function ImageLightbox({
  imagePreview,
  imagePreviewList,
  imagePreviewIndex,
  imagePreviewVisible,
  onClose,
  onSelectIndex,
  onSelectPreview,
}: Props) {
  // Local index drives what's actually rendered — the prop is the source of truth
  // but we keep our own copy so we can animate between changes.
  const [localIdx, setLocalIdx] = useState(imagePreviewIndex);
  // Index of the image sliding IN (null when not animating)
  const [incomingIdx, setIncomingIdx] = useState<number | null>(null);
  // Direction of the slide: 'next' → current slides left, incoming from right
  const [navDir, setNavDir] = useState<'next' | 'prev'>('next');
  // Phase: 'idle' | 'prepare' (incoming rendered off-screen) | 'slide' (both moving)
  const [animPhase, setAnimPhase] = useState<'idle' | 'prepare' | 'slide'>('idle');

  // Touch drag state
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const axisLocked = useRef<'x' | 'y' | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  // A touch drag fires a trailing synthetic click on touchend; this flag lets the
  // backdrop handler ignore that click so a swipe never doubles as an off-click close.
  const suppressClickRef = useRef(false);

  const thumbsRef = useRef<HTMLDivElement>(null);

  const hasMultiple = imagePreviewList.length > 1;
  const count = imagePreviewList.length;

  // Sync if the parent changes the index (e.g. thumbnail click while idle)
  useEffect(() => {
    if (animPhase !== 'idle') return;
    if (imagePreviewIndex === localIdx) return;
    const dir = imagePreviewIndex > localIdx ? 'next' : 'prev';
    triggerNav(imagePreviewIndex, dir);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePreviewIndex]);

  // Scroll active thumbnail into view
  useEffect(() => {
    if (!thumbsRef.current) return;
    const active = thumbsRef.current.children[localIdx] as HTMLElement | undefined;
    active?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [localIdx]);

  if (!imagePreview) return null;

  // ── Navigation ──────────────────────────────────────────────────────────────

  function triggerNav(newIdx: number, dir: 'next' | 'prev') {
    if (animPhase !== 'idle') return;
    setNavDir(dir);
    setIncomingIdx(newIdx);
    setAnimPhase('prepare');
    // One rAF to let the incoming image render at its off-screen start position,
    // then a second rAF to start the CSS transition.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setAnimPhase('slide');
      });
    });
    setTimeout(() => {
      setLocalIdx(newIdx);
      setIncomingIdx(null);
      setAnimPhase('idle');
      onSelectIndex(newIdx);
      onSelectPreview(imagePreviewList[newIdx] ?? null);
    }, ANIM_MS + 20);
  }

  function goPrev() {
    const prev = (localIdx - 1 + count) % count;
    triggerNav(prev, 'prev');
  }

  function goNext() {
    const next = (localIdx + 1) % count;
    triggerNav(next, 'next');
  }

  // ── Touch handlers ──────────────────────────────────────────────────────────

  // Click anywhere outside the image (backdrop / empty stage) closes — but skip
  // the synthetic click that trails a swipe.
  const onBackdropClick = () => {
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    onClose();
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    axisLocked.current = null;
    suppressClickRef.current = false;
    setDragX(0);
    setDragY(0);
    setDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current || animPhase === 'slide') return;
    const t = e.touches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;

    // Lock axis on first meaningful move
    if (!axisLocked.current) {
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
        axisLocked.current = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
        suppressClickRef.current = true; // a real drag began — don't treat its trailing click as a close
      }
    }

    if (axisLocked.current === 'x') {
      setDragX(dx);
      setDragY(0);
    } else if (axisLocked.current === 'y') {
      setDragY(dy);
      setDragX(0);
    }
  };

  const onTouchEnd = () => {
    setDragging(false);
    const dx = dragX;
    const dy = dragY;
    setDragX(0);
    setDragY(0);
    touchStartRef.current = null;

    if (axisLocked.current === 'y' && Math.abs(dy) > SWIPE_Y_THRESHOLD) {
      onClose();
      return;
    }
    if (axisLocked.current === 'x' && Math.abs(dx) > SWIPE_X_THRESHOLD && hasMultiple) {
      if (dx < 0) goNext();
      else goPrev();
    }
    axisLocked.current = null;
  };

  // ── Transform helpers ───────────────────────────────────────────────────────

  // Width of the viewport for off-screen positioning
  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;

  function currentTransform(): string {
    if (dragging && axisLocked.current === 'x') return `translateX(${dragX}px)`;
    if (dragging && axisLocked.current === 'y') return `translateY(${dragY}px)`;
    if (animPhase === 'slide') {
      return navDir === 'next' ? `translateX(-${vw}px)` : `translateX(${vw}px)`;
    }
    return 'translateX(0)';
  }

  function incomingTransform(): string {
    if (animPhase === 'prepare') {
      return navDir === 'next' ? `translateX(${vw}px)` : `translateX(-${vw}px)`;
    }
    if (animPhase === 'slide') return 'translateX(0)';
    return navDir === 'next' ? `translateX(${vw}px)` : `translateX(-${vw}px)`;
  }

  // During horizontal drag show the neighbour peeking in
  function neighbourTransform(): string {
    if (!dragging || axisLocked.current !== 'x') return dragX < 0 ? `translateX(${vw}px)` : `translateX(-${vw}px)`;
    return dragX < 0
      ? `translateX(${dragX + vw}px)` // next peeking from right
      : `translateX(${dragX - vw}px)`; // prev peeking from left
  }

  const isAnimating = animPhase === 'slide';
  const transition = (isAnimating || (!dragging && axisLocked.current === null))
    ? `transform ${ANIM_MS}ms cubic-bezier(0.35, 0, 0.25, 1)`
    : 'none';

  const bgOpacity = dragging && axisLocked.current === 'y'
    ? Math.max(0.2, 1 - Math.abs(dragY) / 250) * 0.85
    : imagePreviewVisible ? 0.85 : 0;

  // Show the dragged neighbour image (different from the animation incoming)
  const dragNeighbourIdx = dragging && axisLocked.current === 'x'
    ? dragX < 0
      ? (localIdx + 1) % count       // dragging left → next
      : (localIdx - 1 + count) % count // dragging right → prev
    : null;

  const showDragNeighbour = dragNeighbourIdx !== null && hasMultiple && dragNeighbourIdx !== localIdx;

  // ── Download ────────────────────────────────────────────────────────────────

  const downloadImage = async () => {
    const url = imagePreview.url;
    const name = filenameFromPreview(imagePreview.name || 'image', url);
    const androidBridge = (window as any)?.EchoAndroid;
    if (androidBridge && typeof androidBridge.downloadImage === 'function') {
      try { androidBridge.downloadImage(new URL(url, window.location.href).toString(), name); return; } catch { /* fall through */ }
    }
    try {
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl; link.download = name;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      const link = document.createElement('a');
      link.href = url; link.download = name; link.target = '_blank'; link.rel = 'noreferrer';
      document.body.appendChild(link); link.click(); link.remove();
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const currentItem = imagePreviewList[localIdx] ?? imagePreview;
  const incomingItem = incomingIdx !== null ? imagePreviewList[incomingIdx] : null;
  const dragNeighbourItem = showDragNeighbour ? imagePreviewList[dragNeighbourIdx!] : null;

  const imgMaxStyle: React.CSSProperties = {
    maxWidth: '90vw',
    maxHeight: hasMultiple ? '72vh' : '80vh',
  };

  return (
    <div
      className="fixed inset-0 z-[40000] flex flex-col items-center justify-center"
      onClick={onBackdropClick}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black transition-opacity duration-200"
        style={{ opacity: bgOpacity }}
      />

      {/* Top bar */}
      <div
        className={`relative z-10 flex w-full max-w-3xl items-center justify-between px-4 pb-2 pt-safe transition-opacity duration-200 ${imagePreviewVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-fg-2 border border-white/10 hover:bg-black/70 transition-colors"
          onClick={(e) => { e.stopPropagation(); void downloadImage(); }}
          aria-label="Download image"
          title="Download image"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
            <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
          </svg>
        </button>
        {hasMultiple && (
          <span className="text-xs text-fg-3">{localIdx + 1} / {count}</span>
        )}
        <button
          className="flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-fg-2 border border-white/10 hover:bg-black/70 hover:text-white transition-colors"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Image stage — overflow hidden so off-screen images are invisible */}
      <div
        className="relative z-10 flex items-center justify-center overflow-hidden"
        style={{ width: '100vw', height: hasMultiple ? '72vh' : '80vh', touchAction: 'none' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Current image */}
        <img
          key={`cur-${localIdx}`}
          src={currentItem.url}
          alt={currentItem.name || 'image'}
          className="absolute block rounded-xl shadow-2xl object-contain"
          style={{ ...imgMaxStyle, transform: currentTransform(), transition, willChange: 'transform' }}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Incoming image (during arrow/thumbnail navigation) */}
        {incomingItem && (
          <img
            key={`inc-${incomingIdx}`}
            src={incomingItem.url}
            alt={incomingItem.name || 'image'}
            className="absolute block rounded-xl shadow-2xl object-contain"
            style={{ ...imgMaxStyle, transform: incomingTransform(), transition: animPhase === 'slide' ? transition : 'none', willChange: 'transform' }}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
          />
        )}

        {/* Drag neighbour (during live touch drag) */}
        {dragNeighbourItem && (
          <img
            key={`drag-nb-${dragNeighbourIdx}`}
            src={dragNeighbourItem.url}
            alt={dragNeighbourItem.name || 'image'}
            className="absolute block rounded-xl shadow-2xl object-contain"
            style={{ ...imgMaxStyle, transform: neighbourTransform(), transition: 'none', willChange: 'transform' }}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
          />
        )}

        {/* Desktop prev/next arrows */}
        {hasMultiple && (
          <>
            <button
              className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 text-fg border border-neutral-700 hover:bg-black/60 hidden sm:flex items-center justify-center z-10"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              aria-label="Previous"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 text-fg border border-neutral-700 hover:bg-black/60 hidden sm:flex items-center justify-center z-10"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              aria-label="Next"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </>
        )}
      </div>

      {/* Hint text */}
      <p className={`relative z-10 mt-2 text-meta text-fg-3 sm:hidden transition-opacity duration-200 ${imagePreviewVisible ? 'opacity-100' : 'opacity-0'}`}>
        {hasMultiple ? 'swipe left / right to browse · swipe up / down to close' : 'swipe up or down to close'}
      </p>

      {/* Thumbnail strip */}
      {hasMultiple && (
        <div
          className={`relative z-10 mt-3 w-full overflow-hidden transition-opacity duration-200 ${imagePreviewVisible ? 'opacity-100' : 'opacity-0'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Outer scrollable — padding lets first/last thumbs scroll to centre */}
          <div
            ref={thumbsRef}
            className="flex gap-2 overflow-x-auto pb-2"
            style={{
              scrollbarWidth: 'none',
              justifyContent: 'center',
              paddingLeft: 'calc(50vw - 28px)',
              paddingRight: 'calc(50vw - 28px)',
              scrollSnapType: 'x mandatory',
            }}
          >
            {imagePreviewList.map((item, idx) => (
              <button
                key={idx}
                className={`shrink-0 rounded-lg overflow-hidden border-2 transition-[transform,border-color] duration-150 ${idx === localIdx ? 'border-brand scale-105' : 'border-transparent opacity-60 hover:opacity-90'}`}
                style={{ width: 56, height: 56, scrollSnapAlign: 'center' }}
                onClick={() => {
                  if (idx === localIdx || animPhase !== 'idle') return;
                  triggerNav(idx, idx > localIdx ? 'next' : 'prev');
                }}
                aria-label={`Image ${idx + 1}`}
              >
                <img
                  src={item.url}
                  alt={item.name || `Image ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  draggable={false}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
